import { useEffect, useRef, useState } from "react";
import { BookOpen, Download, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

const inputCls =
  "w-full rounded-2xl border border-forest/20 bg-white/70 px-4 py-2.5 text-sm outline-none focus:border-gold";

export default function AdminEbook() {
  const [s, setS] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState(null);
  const [saving, setSaving] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [optin, setOptin] = useState("");
  const pdfRef = useRef(null);
  const coverRef = useRef(null);

  const load = () => {
    api.get("/admin/ebook/settings").then(({ data }) => setS(data.settings));
    api.get("/admin/ebook/stats").then(({ data }) => setStats(data));
  };

  const loadLeads = () => {
    const qs = new URLSearchParams(
      Object.entries({ registered_from: from, registered_to: to, optin }).filter(([, v]) => v)
    ).toString();
    api.get(`/admin/ebook/leads?${qs}`).then(({ data }) => setLeads(data.leads));
  };

  useEffect(() => {
    document.title = "Ebook Manager | Rooted Healing Admin";
    load();
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.put("/admin/ebook/settings", { values: patch });
      setS((v) => ({ ...v, ...patch }));
      toast.success("Saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const upload = async (file, key) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/resources/upload", fd);
      await save({ [key]: data.url });
      toast.success(key === "pdf_file" ? "Ebook PDF replaced." : "Cover image replaced.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Upload failed."));
    }
  };

  if (!s) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-forest" />
      </div>
    );
  }

  return (
    <div data-testid="admin-ebook">
      <h1 className="font-serif text-3xl text-forest">The Detox Gospel Ebook Manager</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-taupe">
        Upload the book, shape the landing page wording, and review the people who requested a
        copy. Requesting the ebook never subscribes anyone to marketing emails on its own.
      </p>

      {stats && (
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {[
            { label: "Landing visits", value: stats.landing_views },
            { label: "Requests", value: stats.requests },
            { label: "Downloads", value: stats.downloads },
            { label: "Newsletter opt-ins", value: stats.newsletter_optins },
            { label: "Emails sent", value: stats.delivery_sent },
            { label: "Conversion", value: stats.conversion_rate == null ? "n/a" : `${stats.conversion_rate}%` },
          ].map((x) => (
            <div key={x.label} className="rounded-2xl border border-forest/10 bg-white/75 p-4 text-center">
              <p className="font-serif text-2xl text-forest">{x.value}</p>
              <p className="mt-1 text-[10px] uppercase tracking-wide text-taupe">{x.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-[1.75rem] border border-forest/10 bg-white/75 p-6">
          <h2 className="font-serif text-xl text-forest">Book details</h2>
          <div className="mt-4 flex flex-col gap-3">
            <input data-testid="ebook-title" value={s.title} onChange={(e) => setS({ ...s, title: e.target.value })} placeholder="Title" className={inputCls} />
            <input data-testid="ebook-subtitle" value={s.subtitle} onChange={(e) => setS({ ...s, subtitle: e.target.value })} placeholder="Subtitle (optional)" className={inputCls} />
            <input data-testid="ebook-author" value={s.author} onChange={(e) => setS({ ...s, author: e.target.value })} placeholder="Author" className={inputCls} />
            <textarea data-testid="ebook-description" rows={4} value={s.description} onChange={(e) => setS({ ...s, description: e.target.value })} placeholder="Description shown on the landing page" className={inputCls} />
            <textarea data-testid="ebook-confirmation" rows={2} value={s.confirmation_message} onChange={(e) => setS({ ...s, confirmation_message: e.target.value })} placeholder="Confirmation message after someone requests the ebook" className={inputCls} />
            <button
              data-testid="ebook-save"
              onClick={() => save({ title: s.title, subtitle: s.subtitle, author: s.author, description: s.description, confirmation_message: s.confirmation_message })}
              disabled={saving}
              className="inline-flex w-fit items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-semibold text-ivory hover:bg-forest-deep disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save Wording
            </button>
          </div>
          <div className="mt-6 flex flex-col gap-3 border-t border-forest/10 pt-5">
            <div className="flex flex-wrap items-center gap-3">
              <button data-testid="ebook-upload-pdf" onClick={() => pdfRef.current?.click()} className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-5 py-2.5 text-xs font-semibold text-forest hover:border-gold">
                <Upload className="h-3.5 w-3.5" />
                {s.pdf_file ? "Replace ebook PDF" : "Upload ebook PDF"}
              </button>
              <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "pdf_file")} />
              {s.pdf_file && <span className="text-xs font-semibold text-forest">PDF attached</span>}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button data-testid="ebook-upload-cover" onClick={() => coverRef.current?.click()} className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-5 py-2.5 text-xs font-semibold text-forest hover:border-gold">
                <Upload className="h-3.5 w-3.5" />
                {s.cover_file ? "Replace cover image" : "Upload cover image"}
              </button>
              <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "cover_file")} />
              {s.cover_file && <img src={s.cover_file} alt="Ebook cover preview" className="h-16 rounded-lg" />}
            </div>
          </div>
        </div>

        <div className="rounded-[1.75rem] border border-forest/10 bg-white/75 p-6">
          <h2 className="font-serif text-xl text-forest">Availability</h2>
          <div className="mt-4 flex flex-col gap-4">
            {[
              { key: "published", label: "Published", hint: "The landing page accepts requests. Turn off to pause lead capture." },
              { key: "downloads_enabled", label: "Downloads enabled", hint: "Visitors can download the PDF after submitting the form. Requires an uploaded PDF." },
            ].map((t) => (
              <div key={t.key} className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-forest">{t.label}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-taupe">{t.hint}</p>
                </div>
                <button
                  data-testid={`ebook-toggle-${t.key}`}
                  onClick={() => save({ [t.key]: !s[t.key] })}
                  role="switch"
                  aria-checked={s[t.key]}
                  className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-300 ${s[t.key] ? "bg-forest" : "bg-charcoal/20"}`}
                >
                  <span className={`absolute top-1 h-5 w-5 rounded-full bg-ivory shadow transition-all duration-300 ${s[t.key] ? "left-6" : "left-1"}`} />
                </button>
              </div>
            ))}
          </div>
          <p className="mt-6 rounded-2xl bg-sage/60 p-4 text-xs leading-relaxed text-charcoal/70">
            Delivery: after the form, visitors see a download button immediately, and a branded
            email with a 7-day download link is sent through the configured transactional email
            service. If email is unavailable, the on-page download still works.
          </p>
          <a
            href="/the-detox-gospel"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ebook-preview-page"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-forest underline-offset-4 hover:underline"
          >
            <BookOpen className="h-4 w-4" />
            Preview the landing page
          </a>
        </div>
      </div>

      <div className="mt-8 rounded-[1.75rem] border border-forest/10 bg-white/75 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-serif text-xl text-forest">Ebook leads</h2>
          <a
            data-testid="ebook-leads-export"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/ebook/leads.csv`}
            className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-5 py-2.5 text-xs font-semibold text-forest hover:border-gold"
          >
            <Download className="h-3.5 w-3.5" />
            Export leads CSV
          </a>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-taupe">
          <label className="flex items-center gap-2">
            From
            <input data-testid="leads-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-full border border-forest/20 bg-white/70 px-4 py-2 text-xs text-charcoal outline-none focus:border-gold" />
          </label>
          <label className="flex items-center gap-2">
            To
            <input data-testid="leads-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-full border border-forest/20 bg-white/70 px-4 py-2 text-xs text-charcoal outline-none focus:border-gold" />
          </label>
          <select data-testid="leads-optin" value={optin} onChange={(e) => setOptin(e.target.value)} className="rounded-full border border-forest/20 bg-white/70 px-4 py-2 text-xs text-charcoal outline-none focus:border-gold">
            <option value="">Any newsletter status</option>
            <option value="true">Newsletter opted in</option>
            <option value="false">Not opted in</option>
          </select>
          <button data-testid="leads-apply" onClick={loadLeads} className="rounded-full bg-forest px-5 py-2 text-xs font-semibold text-ivory hover:bg-forest-deep">
            Apply
          </button>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-forest/10 text-xs uppercase tracking-[0.15em] text-taupe">
                <th className="px-3 py-3">Name</th>
                <th className="px-3 py-3">Email</th>
                <th className="px-3 py-3">Phone</th>
                <th className="px-3 py-3">Newsletter</th>
                <th className="px-3 py-3">Requested</th>
                <th className="px-3 py-3">Downloads</th>
              </tr>
            </thead>
            <tbody>
              {(leads || []).map((l) => (
                <tr key={l.lead_id} data-testid={`lead-row-${l.lead_id}`} className="border-b border-forest/5">
                  <td className="px-3 py-3 font-semibold text-forest">{l.first_name} {l.last_name}</td>
                  <td className="px-3 py-3 text-charcoal/75">{l.email}</td>
                  <td className="px-3 py-3 text-charcoal/75">{l.phone || "—"}</td>
                  <td className="px-3 py-3">{l.newsletter_opt_in ? "Yes" : "No"}</td>
                  <td className="px-3 py-3 text-charcoal/75">{fmt(l.submitted_at)}</td>
                  <td className="px-3 py-3 text-charcoal/75">{l.download_count || 0}</td>
                </tr>
              ))}
              {leads && leads.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-taupe">No leads match these filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
